import { apiClient } from "../../lib/http/apiClient";
import type {
  BuyerPreferences,
  BuyerOnboardingOptions,
  InterestGraph,
  MyRecommendationContext,
  SellerPreferenceStatus,
  SellerScore,
  RecommendedFeedsResponse,
  ChatRecommendationResponse,
} from "./types";

export const recommendationsService = {
  async getOnboardingOptions(role: "buyer" | "seller"): Promise<BuyerOnboardingOptions> {
    const { data } = await apiClient.get<BuyerOnboardingOptions>("/api/v1/onboarding/options", {
      params: { role },
    });
    return data;
  },

  async saveBuyerPreferences(preferences: BuyerPreferences): Promise<{ status: string }> {
    const { data } = await apiClient.post<{ status: string }>("/api/v1/buyer/preferences", preferences);
    return data;
  },

  async getBuyerPreferences(): Promise<BuyerPreferences> {
    const { data } = await apiClient.get<BuyerPreferences>("/api/v1/buyer/preferences");
    return data;
  },

  async getBuyerInterestGraph(): Promise<InterestGraph> {
    const { data } = await apiClient.get<InterestGraph>("/api/v1/buyer/interest-graph");
    return data;
  },

  async recordInteraction(interaction: {
    feed_id?: string;
    interaction_type: string;
    meta?: Record<string, any>;
  }): Promise<{ status: string }> {
    const { data } = await apiClient.post<{ status: string }>("/api/v1/buyer/interactions", interaction);
    return data;
  },

  async getRecommendedFeeds(limit = 20, offset = 0): Promise<RecommendedFeedsResponse> {
    const { data } = await apiClient.get<RecommendedFeedsResponse>("/api/v1/buyer/feeds/recommended", {
      params: { limit, offset },
    });
    return data;
  },

  async saveSellerPreferences(preferences: {
    countries: string[];
    cities: string[];
    tags: string[];
    activities: string[];
  }): Promise<{ status: string }> {
    const { data } = await apiClient.post<{ status: string }>("/api/v1/seller/preferences", preferences);
    return data;
  },

  async getSellerPreferenceStatus(): Promise<SellerPreferenceStatus> {
    const { data } = await apiClient.get<SellerPreferenceStatus>("/api/v1/seller/preferences/status");
    return data;
  },

  async getSellerScore(sellerId: string): Promise<SellerScore> {
    const { data } = await apiClient.get<SellerScore>(`/api/v1/seller/${sellerId}/score`);
    return data;
  },

  async triggerMetadataExtraction(dealId: string): Promise<{ status: string }> {
    const { data } = await apiClient.post<{ status: string }>(`/api/recommendations/deals/${dealId}/extract`);
    return data;
  },

  async getMyRecommendationContext(): Promise<MyRecommendationContext> {
    const { data } = await apiClient.get<MyRecommendationContext>("/api/v1/buyer/me");
    return data;
  },

  async getSellerLeadInbox(surface = "all"): Promise<{ leads: any[]; total: number; count: number }> {
    const { data } = await apiClient.get<{ leads: any[]; total: number; count: number }>(
      "/api/v1/seller/lead-inbox",
      { params: { surface } },
    );
    return data;
  },

  async recommendSellersFromChat(message: string, budget?: number, pax?: number): Promise<ChatRecommendationResponse> {
    const { data } = await apiClient.post<ChatRecommendationResponse>("/api/v1/buyer/recommend-sellers", {
      message,
      budget,
      pax,
    });
    return data;
  },
};